// Professional institutional navigation structure
// All links point to actually implemented pages only

export interface NavItem {
  label: string;
  href: string;
  description?: string;
  children?: NavItem[];
  featured?: boolean;
  external?: boolean;
}

export interface NavSection {
  title: string;
  href: string;
  items: NavItem[];
}

// Utility navigation (top bar)
export const utilityNav = {
  quickLinks: [
    { label: 'Donate', href: '/donate', external: false },
    { label: 'Closures', href: '/closures', external: false },
    { label: 'Library', href: '/library', external: false },
  ],
  loginTools: [
    { label: 'Student Portal', href: '/student-experience', description: 'Access your student account and resources' },
    { label: 'Email', href: 'https://mail.google.com', external: true },
    { label: 'LMS', href: 'https://lms.estad.edu', external: true, description: 'Learning Management System' },
    { label: 'Staff Directory', href: '/staff-directory', external: false },
  ],
};

// Main mega-menu navigation — only links to existing/implemented pages
export const mainNavigation: NavSection[] = [
  {
    title: 'Programs & Courses',
    href: '/programs',
    items: [
      {
        label: 'Career & Program Exploration',
        href: '/programs',
        children: [
          { label: 'All Programs', href: '/programs' },
          { label: 'Bachelor Programs', href: '/programs/bachelor' },
          { label: 'Master Programs', href: '/programs/master' },
        ],
      },
      {
        label: 'Continuing Education',
        href: '/programs',
        children: [
          { label: 'Apply Now', href: '/admissions', featured: true },
          { label: 'Scholarships & Aid', href: '/admissions/scholarships' },
          { label: 'Program Fees', href: '/admissions' },
        ],
      },
      {
        label: 'Learning Experience',
        href: '/student-experience',
        children: [
          { label: 'Student Experience', href: '/student-experience' },
          { label: 'Library', href: '/library' },
          { label: 'Research & Innovation', href: '/research' },
          { label: 'Campus Life', href: '/campus' },
        ],
      },
    ],
  },
  {
    title: 'Admissions',
    href: '/admissions',
    items: [
      {
        label: 'Apply to a Program',
        href: '/admissions',
        children: [
          { label: 'Apply Now', href: '/admissions', featured: true },
          { label: 'Future Students', href: '/admissions/future' },
          { label: 'International Students', href: '/admissions/international' },
          { label: 'Admission Requirements', href: '/admissions' },
        ],
      },
      {
        label: 'Financial Support',
        href: '/admissions/scholarships',
        children: [
          { label: 'Scholarships & Bursaries', href: '/admissions/scholarships' },
          { label: 'Tuition & Fees', href: '/admissions' },
          { label: 'Donate to ESTAD', href: '/donate' },
        ],
      },
      {
        label: 'Important Dates',
        href: '/closures',
        children: [
          { label: 'Closures & Holidays', href: '/closures' },
          { label: 'Contact Admissions', href: '/contact' },
          { label: 'Staff Directory', href: '/staff-directory' },
        ],
      },
    ],
  },
  {
    title: 'Student Experience',
    href: '/student-experience',
    items: [
      {
        label: 'Student Supports',
        href: '/student-experience',
        children: [
          { label: 'Academic Advising', href: '/student-experience' },
          { label: 'Career Services', href: '/student-experience' },
          { label: 'Counseling & Wellness', href: '/student-experience' },
          { label: 'Accessibility Services', href: '/student-experience' },
          { label: 'International Support', href: '/admissions/international' },
        ],
      },
      {
        label: 'Campus Life',
        href: '/campus',
        children: [
          { label: 'Campus Gallery', href: '/campus' },
          { label: 'Housing', href: '/student-experience' },
          { label: 'Student Association', href: '/student-experience' },
          { label: 'Clubs & Activities', href: '/student-experience' },
        ],
      },
      {
        label: 'Academic Resources',
        href: '/library',
        children: [
          { label: 'Library', href: '/library' },
          { label: 'Research & Innovation', href: '/research' },
          { label: 'Partnerships', href: '/partnerships' },
        ],
      },
    ],
  },
  {
    title: 'Campuses',
    href: '/campuses',
    items: [
      {
        label: 'Our Campuses',
        href: '/campuses',
        children: [
          { label: 'All Campuses', href: '/campuses' },
          { label: 'Campus Gallery & Life', href: '/campus' },
        ],
      },
      {
        label: 'Campus Services',
        href: '/campuses',
        children: [
          { label: 'Library', href: '/library' },
          { label: 'Closures & Alerts', href: '/closures' },
          { label: 'Contact a Campus', href: '/contact' },
        ],
      },
    ],
  },
  {
    title: 'About',
    href: '/about',
    items: [
      {
        label: 'Who We Are',
        href: '/about',
        children: [
          { label: 'About ESTAD', href: '/about' },
          { label: 'Why Choose ESTAD', href: '/about/why-estad' },
          { label: 'Staff Directory', href: '/staff-directory' },
        ],
      },
      {
        label: 'Community',
        href: '/about',
        children: [
          { label: 'News & Events', href: '/news' },
          { label: 'Partnerships', href: '/partnerships' },
          { label: 'Research & Innovation', href: '/research' },
          { label: 'Donate', href: '/donate' },
        ],
      },
      {
        label: 'Connect',
        href: '/contact',
        children: [
          { label: 'Contact Us', href: '/contact' },
          { label: 'Closures & Alerts', href: '/closures' },
          { label: 'Library', href: '/library' },
        ],
      },
    ],
  },
];

// Footer navigation
export const footerNavigation = {
  quickLinks: [
    { label: 'Jobs', href: '/staff-directory' },
    { label: 'Media', href: '/news' },
    { label: 'Foundation', href: '/donate' },
    { label: 'Alumni', href: '/about' },
    { label: 'Employee directory', href: '/staff-directory' },
    { label: 'Contact', href: '/contact' },
  ],
  legal: [
    { label: 'Privacy', href: '/contact' },
    { label: 'Disclaimer', href: '/contact' },
    { label: 'Sitemap', href: '/' },
  ],
  social: [
    { label: 'Facebook', href: 'https://facebook.com/estad', icon: 'facebook' },
    { label: 'Instagram', href: 'https://instagram.com/estad', icon: 'instagram' },
    { label: 'LinkedIn', href: 'https://linkedin.com/school/estad', icon: 'linkedin' },
    { label: 'YouTube', href: 'https://youtube.com/estad', icon: 'youtube' },
    { label: 'TikTok', href: 'https://tiktok.com/@estad', icon: 'tiktok' },
  ],
};
